import React, { PureComponent } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import {
  Button,
  Collapse,
  Container,
  Navbar,
  NavbarToggler,
  Nav,
  NavLink,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import ProfileMenu from '../ProfileMenu';
import SearchV2 from '../SearchV2';
import { APIgetPage } from '../../api/Pages';
import SidebarFiltersSwitch from '../Sidebar/FiltersSwitch';

import { setSearchTerm, setSearchFields } from '../../actions/Search';
import { clearFilesFilters, clearSelectedFilters } from '../../actions/Files';
import { setSidebarActiveTab, setSidebarOpen, setSecondSidebarOpen } from '../../actions/App';
import { slugify } from '../../helpers';
import { getSetting } from '../../helpers/Settings';
import { isValidUrl } from '../../helpers/Validators';
import {
  getCollectionDestination,
  getFileDestination,
  normalizePageVisibilityMode,
} from '../../helpers/PageSections';
import { normalizeHydratedPageFile } from './Components/Common/pageFileHydration';
import { getPageFileByUuid } from './Components/Common/pageFiles';

const pageSlugByIdCache = new Map();

const toPagePath = (pageRouteToken, pageBasePath = 'pages') => {
  if (pageRouteToken == null || pageRouteToken === '') return '';

  const routeToken = String(pageRouteToken);
  if (isValidUrl(routeToken)) return routeToken;

  if (routeToken.startsWith('/')) {
    return routeToken;
  }

  return `/${pageBasePath}/${routeToken}`;
};

const normalizePageNavigationItem = (item = {}) => ({
  title: item.title || '',
  className: item.className || item.class || '',
  targetType: item.targetType || 'url',
  targetCollection: item.targetCollection || null,
  targetFile: item.targetFile || null,
  targetPage: item.targetPage || null,
  url: item.url || '',
});

const resolvePageNavigationDestination = ({
  item,
  pageVisibilityMode,
  pageBasePath,
  pageRouteId,
  pageId,
  backUrl,
}) => {
  if (!item) {
    return '';
  }

  if (item.targetType === 'collection' && item.targetCollection?.uuid) {
    return getCollectionDestination({
      collection: item.targetCollection,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
    });
  }

  if (item.targetType === 'file' && item.targetFile?.uuid) {
    return getFileDestination({
      file: item.targetFile,
      pageVisibilityMode,
      pageBasePath,
      pageRouteId,
      pageId,
      backUrl,
    });
  }

  if (item.targetType === 'page' && (item.targetPage?.id || item.targetPage?.url)) {
    const pageRouteToken = item.targetPage?.slugResolved
      ? (item.targetPage?.url || item.targetPage?.id)
      : (item.targetPage?.id || item.targetPage?.url);

    return toPagePath(pageRouteToken, pageBasePath);
  }

  if (item.targetType === 'url' && item.url) {
    return item.url;
  }

  return '';
};

class PageHeader extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      isSearchOpen: false,
      resolvedPageNavigation: Array.isArray(props.pageNavigation) ? props.pageNavigation : [],
    };

    this.isMountedFlag = false;

    this.toggle = this.toggle.bind(this);
    this.toggleSearch = this.toggleSearch.bind(this);
    this.resolvePageNavigation = this.resolvePageNavigation.bind(this);
    this.getPageNavigationItems = this.getPageNavigationItems.bind(this);
  }

  componentDidMount() {
    this.isMountedFlag = true;
    const { pageNavigation } = this.props;
    this.resolvePageNavigation(pageNavigation);
  }

  componentDidUpdate(prevProps) {
    const { pageNavigation } = this.props;
    if (prevProps.pageNavigation !== pageNavigation) {
      this.resolvePageNavigation(pageNavigation);
    }
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
  }

  getPageNavigationItems() {
    const {
      pageBasePath,
      pageRouteId,
      pageId,
      pageVisibilityMode,
      location,
    } = this.props;
    const { resolvedPageNavigation } = this.state;

    const normalizedVisibilityMode = normalizePageVisibilityMode(pageVisibilityMode);
    const pathname = location?.pathname || '';
    const search = location?.search || '';
    const backUrl = `${pathname}${search}`;

    return (resolvedPageNavigation || [])
      .map(item => normalizePageNavigationItem(item))
      .map((item) => {
        const url = resolvePageNavigationDestination({
          item,
          pageVisibilityMode: normalizedVisibilityMode,
          pageBasePath,
          pageRouteId,
          pageId,
          backUrl,
        });

        return {
          title: item.title,
          class: item.className,
          targetType: item.targetType,
          url,
        };
      })
      .filter(item => item.url);
  }

  resolvePageNavigation(pageNavigation) {
    const { pageId } = this.props;
    const sourceItems = Array.isArray(pageNavigation)
      ? pageNavigation.map(item => normalizePageNavigationItem(item))
      : [];

    const pageItems = sourceItems.filter(
      item => item.targetType === 'page' && item.targetPage?.id,
    );
    const fileItems = sourceItems.filter(
      item => item.targetType === 'file' && item.targetFile?.uuid,
    );

    if (pageItems.length === 0 && (fileItems.length === 0 || !pageId)) {
      this.setState({ resolvedPageNavigation: sourceItems });
      return;
    }

    const pagePromise = Promise.all(pageItems.map((item) => {
      const pageTargetId = item.targetPage?.id;
      if (!pageTargetId) {
        return Promise.resolve(null);
      }

      const cacheKey = String(pageTargetId);
      const cachedSlug = pageSlugByIdCache.get(cacheKey);

      return APIgetPage(pageTargetId)
        .then((response) => {
          const currentSlug = response?.data?.url || cachedSlug || item.targetPage?.url || '';
          if (currentSlug) {
            pageSlugByIdCache.set(cacheKey, currentSlug);
          }

          return {
            id: pageTargetId,
            slug: currentSlug,
          };
        })
        .catch(() => ({
          id: pageTargetId,
          slug: cachedSlug || item.targetPage?.url || '',
        }));
    }));

    const filePromise = (!pageId || fileItems.length === 0)
      ? Promise.resolve([])
      : Promise.all(fileItems.map((item) => getPageFileByUuid({
        pageId,
        fileUuid: item.targetFile.uuid,
        fallbackFile: item.targetFile,
      })
        .then((resolved) => ({
          uuid: item.targetFile.uuid,
          file: normalizeHydratedPageFile(item.targetFile, resolved || item.targetFile),
        }))
        .catch(() => ({
          uuid: item.targetFile.uuid,
          file: item.targetFile,
        }))));

    Promise.all([pagePromise, filePromise])
      .then(([resolvedPages, resolvedFiles]) => {
        if (!this.isMountedFlag) {
          return;
        }

        const slugByPageId = {};
        resolvedPages.forEach((entry) => {
          if (entry?.id) {
            slugByPageId[String(entry.id)] = entry.slug || '';
          }
        });

        const fileByUuid = {};
        resolvedFiles.forEach((entry) => {
          if (entry?.uuid) {
            fileByUuid[String(entry.uuid)] = entry.file;
          }
        });

        this.setState({
          resolvedPageNavigation: sourceItems.map((item) => {
            if (item.targetType === 'file' && item.targetFile?.uuid) {
              const hydratedFile = fileByUuid[String(item.targetFile.uuid)] || item.targetFile;

              return {
                ...item,
                targetFile: hydratedFile,
              };
            }

            if (item.targetType !== 'page' || !item.targetPage?.id) {
              return item;
            }

            const resolvedSlug = slugByPageId[String(item.targetPage.id)]
              || item.targetPage.url
              || '';

            return {
              ...item,
              targetPage: {
                ...item.targetPage,
                url: resolvedSlug,
                slugResolved: true,
              },
            };
          }),
        });
      });
  }

  toggle() {
    this.setState(prevState => ({
      isOpen: !prevState.isOpen,
      isSearchOpen: !prevState.isOpen ? false : prevState.isSearchOpen,
    }));
  }

  toggleSearch() {
    this.setState(prevState => ({
      isSearchOpen: !prevState.isSearchOpen,
      isOpen: !prevState.isSearchOpen ? false : prevState.isOpen,
    }));
  }

  resetSearchState = () => {
    const { dispatch } = this.props;
    dispatch(setSearchTerm(''));
    dispatch(setSearchFields([]));
    dispatch(clearFilesFilters());
    dispatch(clearSelectedFilters());
    dispatch(setSidebarActiveTab('1'));
    dispatch(setSidebarOpen(false));
    dispatch(setSecondSidebarOpen(false));
  };

  render() {
    const {
      intl,
      profile,
      headerType,
      pageLogoUrl,
      showFilterSwitch,
      showSearchBar,
      limited,
      pageRouteId,
      pageId,
      pageBasePath,
      filesView,
    } = this.props;
    const {
      isOpen,
      isSearchOpen,
    } = this.state;
    const pageSearchPath = (pageRouteId || pageId)
      ? `/${pageBasePath}/${pageRouteId || pageId}/search`
      : null;
    const pagePath = (pageRouteId || pageId)
      ? `/${pageBasePath}/${pageRouteId || pageId}`
      : '/';

    const loggedIn = profile && profile.uuid;
    const customNavigation = getSetting('custom_navigation');
    const workspaceNavigation = customNavigation ? customNavigation
      .filter(c => c.home_page)
      .filter(c => (
        (c.logged_in === false && !loggedIn) ||
        (c.logged_in === true && loggedIn) ||
        c.logged_in === undefined
      )) : null;
    const pageNavigationItems = this.getPageNavigationItems();
    let navigation = null;
    if (workspaceNavigation && workspaceNavigation.length > 0) {
      navigation = workspaceNavigation;
    } else if (pageNavigationItems.length > 0) {
      navigation = pageNavigationItems;
    }

    let logoUrl = pageLogoUrl;
    if (!logoUrl && profile) {
      logoUrl = headerType === 'solid' ? profile.client.header_dark_logo_url : profile.client.header_light_logo_url;
      if (!logoUrl) {
        logoUrl = profile.client.header_dark_logo_url || profile.client.header_light_logo_url;
      }
    }

    return (
      <div
        className={classnames('toolbar landing-toolbar', headerType, {
          'logged-in': loggedIn,
          'not-logged-in': !loggedIn,
        })}
      >
        <Container>
          <Navbar dark expand="md" className="landing-navbar">
            {profile && (
              <Link to={pagePath} className="landing-logo" onClick={this.resetSearchState} aria-label={intl.formatMessage({ defaultMessage: 'Home' })}>
                <img src={logoUrl} alt={profile.client.name} />
              </Link>
            )}
            <div className="navbar-togglers">
              <Button className="navbar-toggler navbar-search-toggler" onClick={this.toggleSearch} aria-label={isSearchOpen ? intl.formatMessage({ defaultMessage: 'Close search' }) : intl.formatMessage({ defaultMessage: 'Open search' })}>
                <span className="fas fa-search" />
              </Button>
              <NavbarToggler onClick={this.toggle} aria-label={isOpen ? intl.formatMessage({ defaultMessage: 'Close menu' }) : intl.formatMessage({ defaultMessage: 'Open menu' })} />
            </div>
            <Collapse isOpen={isOpen} navbar className="nav-actions nav-actions-mobile">
              <Nav className="nav-main">
                {!navigation || navigation.length === 0 ? (
                  <>
                    {getSetting('cs1') && (
                    <li>
                      <NavLink
                        to="/files"
                        tag={Link}
                      >
                        {intl.formatMessage({ defaultMessage: 'Browse Images' })}
                      </NavLink>
                    </li>
                    )}
                  </>
                ) : (
                  <>
                    {navigation.map((nitem, idx) => {
                      const destination = nitem.url;
                      const shouldUseAnchor = nitem.targetType === 'url';

                      return (
                        <li key={`${slugify(nitem.title)}-${idx + 1}`} className={nitem.class}>
                          {shouldUseAnchor ? (
                            <a
                              href={destination}
                              target={isValidUrl(destination) ? '_blank' : undefined}
                              rel={isValidUrl(destination) ? 'noopener noreferrer' : undefined}
                              className="nav-link"
                            >
                              {nitem.title}
                            </a>
                          ) : (
                            <NavLink
                              to={destination || '#'}
                              tag={Link}
                            >
                              {nitem.title}
                            </NavLink>
                          )}
                        </li>
                      );
                    })}
                  </>
                )}
              </Nav>
              <Nav navbar>
                <ProfileMenu />
              </Nav>
            </Collapse>
            <Collapse isOpen={isOpen} navbar className="nav-actions nav-actions-desktop">
              {!limited && (
                <Nav className="nav-main">
                  {!navigation || navigation.length === 0 ? (
                    <>
                      {getSetting('cs1') && (
                      <li>
                        <NavLink
                          to="/files"
                          tag={Link}
                        >
                          {intl.formatMessage({ defaultMessage: 'Browse Images' })}
                        </NavLink>
                      </li>
                      )}
                    </>
                  ) : (
                    <>
                      {navigation.map((nitem, idx) => {
                        const destination = nitem.url;
                        const shouldUseAnchor = nitem.targetType === 'url';

                        return (
                          <li key={`${slugify(nitem.title)}-${idx + 1}`} className={nitem.class}>
                            {shouldUseAnchor ? (
                              <a
                                href={destination}
                                target={isValidUrl(destination) ? '_blank' : undefined}
                                rel={isValidUrl(destination) ? 'noopener noreferrer' : undefined}
                                className="nav-link"
                              >
                                {nitem.title}
                              </a>
                            ) : (
                              <NavLink
                                to={destination || '#'}
                                tag={Link}
                              >
                                {nitem.title}
                              </NavLink>
                            )}
                          </li>
                        );
                      })}
                    </>
                  )}
                </Nav>
              )}
              <Nav navbar>
                <ProfileMenu />
              </Nav>
            </Collapse>
          </Navbar>
        </Container>
        {showSearchBar !== false && (
          <>
            <Container className="landing-toolbar-secondary">
              {showFilterSwitch && (
                <SidebarFiltersSwitch pageId={pageRouteId || pageId} view={filesView} />
              )}
              <SearchV2
                isOpen={isSearchOpen}
                pageSearchPath={pageSearchPath}
              />
            </Container>
            <Container className="landing-search-filters-placeholder" />
          </>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

PageHeader.propTypes = {
  intl: PropTypes.object.isRequired,
  profile: PropTypes.object,
  headerType: PropTypes.string,
  limited: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
  pageLogoUrl: PropTypes.string,
  showFilterSwitch: PropTypes.bool,
  showSearchBar: PropTypes.bool,
  pageNavigation: PropTypes.array,
  pageBasePath: PropTypes.string,
  pageRouteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pageVisibilityMode: PropTypes.string,
  filesView: PropTypes.string,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
};

PageHeader.defaultProps = {
  profile: null,
  headerType: 'solid',
  pageLogoUrl: null,
  showFilterSwitch: true,
  showSearchBar: true,
  pageNavigation: [],
  pageBasePath: 'pages',
  pageRouteId: null,
  pageId: null,
  pageVisibilityMode: null,
  filesView: null,
  location: null,
  limited: false,
};

export default withRouter(connect(mapStateToProps)(injectIntl(PageHeader)));
